import React from 'react';
import FeeTypesEnum from '../../../Enums/FeeTypesEnum';
import InputTypesEnum from '../../../Enums/InputTypesEnum';
import DataSourceEnum from '../../../Enums/DataSourceEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import selectHelper from '../../../Helpers/selectHelper';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import FreelancerFeeItem from '../../../Domain/FreelancerFeeItem';
import FreelancerFeeService from '../../../Services/FreelancerFeeService';

export default class AddNewFreelancerFeeWindowDescription {
  static titleForCreate = 'Add New Fee';
  // Getter (not an eager static field): FreelancerFeeItem <-> FreelancerUserAdmin form an import
  // cycle, so referencing FreelancerFeeItem at class-definition time throws a TDZ ReferenceError.
  static get itemType() {
    return FreelancerFeeItem;
  }
  static finishButtonLabel = 'Save Fee';
  static messageOnSuccess = 'Fee has been saved.';

  // Mirrors the old SaveFee: apply the fee-type rules (DQ clears self-assigned/calls-emails,
  // Resource clears the opt fee) before posting. ConvertToJSON in the service parseInt's the
  // survey ids, so the old '//' split is unnecessary. MUST return the promise (wizard contract).
  static serviceFunction = (edited) => FreelancerFeeService.AddNewFreelancerFeeItem(FreelancerFeeItem.applyRules(edited));

  // Old panel showed a confirm dialog when the user never touched the Start Date. We approximate
  // "untouched" as "still today's date" (its default), passed to the wizard's approveBeforeSaveCheck.
  static startDateUnchanged = (edited) =>
    dateTimeHelper.toYYYYsMMsDD(edited.StartDate) === dateTimeHelper.toYYYYsMMsDD(new Date());

  static startDateUnchangedMessage = "You haven't modified the Start Date.";

  // Builds the edited item from the selected vendor. Carries the vendor's role flags (the Fee Type
  // option list reads them), currency code (fee labels) and time-based capability onto the item so
  // the static field descriptors can stay per-item pure.
  static buildItemForUser(user) {
    const item = new FreelancerFeeItem();
    item.FreelancerUserId = user.FreelancerUserId;
    item.FreelancerUserName = `${user.FirstName} ${user.LastName}`;
    item.FeeType = AddNewFreelancerFeeWindowDescription.calculateDefaultFeeType(user);
    item.FreelancerUserTimebasedSettlementEnabled = user.TimebasedSettlementEnabled;
    item.FreelancerUserCurrencyCode = user.CurrencyCode;
    item.IsRM = user.IsRM;
    item.IsDQ = user.IsDQ;
    item.IsKAM = user.IsKAM;
    item.IsBD = user.IsBD;
    item.IsBI = user.IsBI;
    item.IsIT = user.IsIT;
    item.IsHR = user.IsHR;
    item.IsMarketing = user.IsMarketing;
    item.IsFinance = user.IsFinance;
    item.IsFieldworkPartner = user.IsFieldworkPartner;
    item.UseOptFee = false; // transient UI toggle mirroring OptFee >= 0
    return item;
  }

  static calculateDefaultFeeType(user) {
    if (user.RmFreelancerUserManagers && user.RmFreelancerUserManagers.length > 0) return FeeTypesEnum.Resource;
    if (user.DqFreelancerUserManagers && user.DqFreelancerUserManagers.length > 0) return FeeTypesEnum.DataQuality;
    return '';
  }

  static isTimebased = (item) => item.TimebasedSettlementEnabled === true;
  static isDataQuality = (item) => item.FeeType === FeeTypesEnum.DataQuality;
  static isThirdParty = (item) => item.Source === DataSourceEnum.ThirdParty;

  static pagesInGeneralEditWizzard = [
    {
      label: 'General',
      fields: [
        {
          fieldName: 'FreelancerUserId',
          label: 'Vendor Id',
          className: 'col-12',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FreelancerUserName',
          label: 'Vendor Name',
          className: 'col-12',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FeeType',
          label: 'Fee Type',
          className: 'col-12',
          type: InputTypesEnum.Select,
          selectvalues: (item) => selectHelper.getSelectOptionsForFeeType(item),
          disableClearButton: true,
          validityChecker: ValidityHelper.forRequiredSelectField,
        },
        {
          fieldName: 'TimebasedSettlementEnabled',
          label: 'Timebased Settlement',
          className: 'col-12',
          type: InputTypesEnum.OnOff,
          validityChecker: AddNewFreelancerFeeWindowDescription.validityCheckerForTimebased,
        },
        {
          fieldName: 'CountryId',
          label: 'Country',
          className: 'col-12',
          type: InputTypesEnum.CountrySelect,
          hide: AddNewFreelancerFeeWindowDescription.isTimebased,
        },
        {
          fieldName: 'SelfAssigned',
          label: 'Self Assigned',
          className: 'col-12',
          type: InputTypesEnum.Bool,
          hide: AddNewFreelancerFeeWindowDescription.isTimebased,
          disabled: (item) => item.FeeType !== FeeTypesEnum.Resource,
        },
        {
          fieldName: 'SurveyTypeCallsEmails',
          label: 'Calls Emails',
          className: 'col-12',
          type: InputTypesEnum.Bool,
          hide: AddNewFreelancerFeeWindowDescription.isTimebased,
          disabled: (item) => item.FeeType !== FeeTypesEnum.Resource,
        },
        {
          fieldName: 'StartDate',
          label: 'Start',
          className: 'col-12',
          type: InputTypesEnum.Date,
          validityChecker: AddNewFreelancerFeeWindowDescription.validityCheckerForDates,
        },
        {
          fieldName: 'EndDate',
          label: 'End',
          className: 'col-12',
          type: InputTypesEnum.DateWithIndefinite,
          validityChecker: AddNewFreelancerFeeWindowDescription.validityCheckerForDates,
        },
        {
          fieldName: 'Fee',
          label: (item) =>
            `Fee (${item.FreelancerUserCurrencyCode})` + (item.TimebasedSettlementEnabled ? '/hour' : ''),
          className: 'col-12',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          validityChecker: AddNewFreelancerFeeWindowDescription.validityCheckerForFee,
        },
        {
          fieldName: 'Tier',
          label: 'Tier',
          className: 'col-12',
          type: InputTypesEnum.Select,
          selectvalues: () => FreelancerFeeItem.getTierSelectOptions(),
          hide: (item) => !AddNewFreelancerFeeWindowDescription.isDataQuality(item),
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'UseOptFee',
          label: 'Use Opt Fee',
          className: 'col-12',
          type: InputTypesEnum.OnOff,
          hide: (item) => !AddNewFreelancerFeeWindowDescription.isDataQuality(item),
          onFieldChange: AddNewFreelancerFeeWindowDescription.onUseOptFeeChange,
        },
        {
          fieldName: 'OptFee',
          label: (item) => `Opt Fee (${item.FreelancerUserCurrencyCode})`,
          className: 'col-12',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          hide: (item) => !AddNewFreelancerFeeWindowDescription.isDataQuality(item) || parseFloat(item.OptFee) < 0,
          info: 'Opt Fee is only applied if the starting status is Reviewed.',
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'ClientId',
          label: 'Client',
          className: 'col-12',
          type: InputTypesEnum.ClientSelect,
          validityChecker: AddNewFreelancerFeeWindowDescription.validityCheckerForClient,
          onFieldChange: AddNewFreelancerFeeWindowDescription.onClientChange,
        },
        {
          fieldName: 'Source',
          label: 'Source',
          className: 'col-12',
          type: InputTypesEnum.SourceSelect,
        },
        {
          fieldName: 'SurveyId',
          label: 'Survey',
          className: 'col-12',
          type: InputTypesEnum.SurveySelect,
          hide: AddNewFreelancerFeeWindowDescription.isThirdParty,
          disabled: (item) => !item.ClientId,
          idFilter: 'ClientId',
        },
        {
          fieldName: 'TpSurveyId',
          label: 'TpSurvey',
          className: 'col-12',
          type: InputTypesEnum.TpSurveySelect,
          hide: (item) => !AddNewFreelancerFeeWindowDescription.isThirdParty(item),
          disabled: (item) => !item.ClientId,
          idFilter: 'ClientId',
        },
        {
          fieldName: 'WaveId',
          label: 'Wave',
          className: 'col-12',
          type: InputTypesEnum.WaveSelect,
          hide: (item) =>
            AddNewFreelancerFeeWindowDescription.isTimebased(item) ||
            AddNewFreelancerFeeWindowDescription.isThirdParty(item),
          disabled: (item) => !(parseInt(item.ClientId) > 0),
          idFilter: 'ClientId',
        },
      ],
    },
  ];

  static onUseOptFeeChange(item) {
    // Enabling starts the opt fee at 0; disabling marks it unused (-1) so it posts as null.
    item.OptFee = item.UseOptFee ? (parseFloat(item.OptFee) >= 0 ? item.OptFee : '0') : '-1';
  }

  static onClientChange(item) {
    // Client drives the survey/wave filters, so clear the dependent selections when it changes.
    item.SurveyId = '';
    item.TpSurveyId = '';
    item.WaveId = '';
    item.TpWaveId = '';
  }

  static validityCheckerForTimebased(e, item) {
    if (
      !item.TimebasedSettlementEnabled &&
      item.FeeType !== FeeTypesEnum.Resource &&
      item.FeeType !== FeeTypesEnum.DataQuality
    ) {
      return 'In case of this fee type Timebased Settlement has to be enabled.';
    }
    if (item.TimebasedSettlementEnabled && !item.FreelancerUserTimebasedSettlementEnabled) {
      return 'Allow the time-based settlement on the vendor profile.';
    }
    return '';
  }

  static validityCheckerForFee(e, item) {
    return item.TimebasedSettlementEnabled
      ? ValidityHelper.forRequiredNumberFieldGreaterThanZero(item.Fee)
      : ValidityHelper.forRequiredNumberFieldAtLeastZero(item.Fee);
  }

  static validityCheckerForDates(e, item) {
    return item.StartDate > item.EndDate ? 'The End Date has to be later than the Start Date.' : '';
  }

  static validityCheckerForClient(e, item) {
    if (item.FeeType !== FeeTypesEnum.DataQuality || item.TimebasedSettlementEnabled === true) {
      return '';
    }
    const intval = parseInt(e);
    if (isNaN(intval) || intval < 1) {
      return 'If the FeeType is DataQuality, a client has to be choosen.';
    }
    return '';
  }
}
